import { action, json, query, redirect } from '@solidjs/router';
import { decode } from 'decode-formdata';
import { getRequestEvent, isServer } from 'solid-js/web';
import { z } from 'zod';
import { ApiError, UnknownApiError } from './errors';
import type { IError } from './errors';

type SupportedMethod = 'get' | 'patch' | 'post' | 'put' | 'delete';
type EndpointDescriptor = {
	parameters?: z.ZodTypeAny;
};
type EndpointByMethod = Record<SupportedMethod, Record<string, EndpointDescriptor>>;
type CacheLike =
	| {
			has(key: unknown): boolean;
			get(key: unknown): unknown;
			set(key: unknown, value: unknown): unknown;
	  }
	| null
	| undefined;

export type GetOptions = {
	requireAuth?: boolean;
	redirect401?: boolean;
};

function redirect401(opts?: GetOptions) {
	if (opts?.redirect401) {
		return;
	}
	const url = isServer ? new URL(getRequestEvent()!.request.url) : new URL(window.location.toString());
	if (url.pathname.startsWith('/auth')) {
		return;
	}
	if (!url.searchParams.get('redirect')) {
		url.searchParams.set('redirect', url.pathname);
	}
	return redirect(`/auth/login?${url.searchParams.toString()}`);
}

export function createCachedGet(args: {
	endpointByMethod: EndpointByMethod;
	getApiClient: () => {
		GET(path: string, init?: unknown): Promise<{ error?: unknown; response: { status: number }; data?: unknown }>;
	};
	getCache: () => CacheLike;
}) {
	return function cachedGet(path: string, rest?: unknown, opts?: GetOptions, key?: string) {
		const cacheFn: ReturnType<typeof query> = query(async (params: unknown): Promise<unknown> => {
			const cache = args.getCache();
			if (cache?.has(cacheFn.keyFor(params))) {
				return cache.get(cacheFn.keyFor(params));
			}

			const validator = args.endpointByMethod.get[path];
			if (validator?.parameters instanceof z.ZodObject) {
				try {
					validator.parameters.parse(params);
				} catch {
					throw new UnknownApiError({});
				}
			}

			const client = args.getApiClient();
			const res = await client.GET(path, { ...((rest as Record<string, unknown> | undefined) ?? {}), params });

			if (res.error) {
				if (res.response.status === 401 && opts?.redirect401 !== false) {
					throw redirect401(opts);
				}
				if (res.response.status === 401 && opts?.redirect401 === false) {
					return undefined;
				}
				throw new ApiError(res.response.status, res.error as IError);
			}

			if (!res.data) {
				throw new UnknownApiError();
			}

			cache?.set(cacheFn.keyFor(params), res.data);

			return res.data;
		}, key ?? path);

		return cacheFn;
	};
}

type RequestActionOptions<Res extends Record<string, unknown>> = {
	revalidate?: string | Array<string> | ((data: Res, input: FormData) => Array<string> | undefined);
	redirect?: (data: Res, input: FormData) => undefined | string;
	onSuccess?: (data: Res, input: FormData) => Promise<void>;
	transformer?: (input: FormData) => Record<string, unknown>;
};

export function createRequestActions(args: {
	endpointByMethod: EndpointByMethod;
	getApiClient: () => unknown;
	revalidate: (key?: unknown, force?: boolean) => Promise<void>;
}) {
	function makeRequest(
		method: SupportedMethod,
		path: string,
		rest?: Record<string, unknown> | ((data: FormData) => Record<string, unknown> | undefined),
		opts?: RequestActionOptions<Record<string, unknown>>,
	) {
		return async (data: FormData) => {
			const client = args.getApiClient();
			const isFormData = data instanceof FormData;
			const isSearchData = data instanceof URLSearchParams;
			const tempDecoded = (isFormData || isSearchData ? decode(data as FormData) : data) as Record<string, unknown>;

			const transformed =
				opts?.transformer?.(
					data instanceof URLSearchParams || data instanceof FormData ? (data as FormData) : new FormData(),
				) ?? {};

			const reqData = {
				...tempDecoded,
				...transformed,
				query: {
					...((tempDecoded?.query as Record<string, unknown> | undefined) ?? {}),
					...tempDecoded,
					...transformed,
				},
				body: { ...((tempDecoded?.body as Record<string, unknown> | undefined) ?? {}), ...tempDecoded, ...transformed },
				path: { ...((tempDecoded?.path as Record<string, unknown> | undefined) ?? {}), ...tempDecoded, ...transformed },
			};

			const validator = args.endpointByMethod[method]?.[path];
			const reqInit = (validator?.parameters?.safeParse(reqData)?.data ?? reqData) as Record<string, unknown>;

			const requestInit = {
				body: reqInit.body,
				headers: reqInit.header,
				params: {
					path: reqInit.path ?? {},
					query: reqInit.query ?? {},
				},
			};

			const requestMethod = (
				client as Record<
					string,
					(
						path: string,
						init?: unknown,
					) => Promise<{
						error?: unknown;
						response: { status: number };
						data?: unknown;
					}>
				>
			)[method.toUpperCase()]!;
			const res = await requestMethod(path, {
				...requestInit,
				...(typeof rest === 'function' ? rest(data) : rest),
			});

			if (res.error) {
				throw new ApiError(res.response.status, res.error as IError);
			}

			if (res.response.status !== 204 && !res.data) {
				throw new UnknownApiError();
			}

			const resData = (res.data ?? {}) as Record<string, unknown>;

			const revalidateData = typeof opts?.revalidate === 'function' ? opts.revalidate(resData, data) : opts?.revalidate;

			if (revalidateData || 'revalidate' in (opts ?? {})) {
				await args.revalidate(revalidateData, true);
			}

			if (opts?.onSuccess) {
				await opts.onSuccess(resData, data);
			}

			const redirectUrl = opts?.redirect ? opts.redirect(resData, data) : undefined;
			if (redirectUrl) {
				await new Promise<void>((resolve) => {
					setTimeout(() => resolve(), 100);
				});
				throw redirect(redirectUrl);
			}

			return resData;
		};
	}

	function clientAction(
		method: SupportedMethod,
		path: string,
		rest?: Record<string, unknown> | ((data: FormData) => Record<string, unknown> | undefined),
		opts?: RequestActionOptions<Record<string, unknown>>,
		key?: string,
	) {
		const req = makeRequest(method, path, rest, opts);
		return action(
			async (data: FormData) => {
				const res = await req(data);
				return json(res, { revalidate: 'nothing' });
			},
			key ?? `${method}-${path}`,
		);
	}

	return {
		makeRequest,
		clientAction,
	};
}
